type PingHandler = () => unknown | Promise<unknown>;

type ChannelMessageType = 'leader-active' | 'leader-inactive' | 'leader-selection' | 'ping-stop';

type ChannelMessage = {
    type: ChannelMessageType,
    tabId: string,
    startedAt: number,
    timestamp: number,
};

const CHANNEL_NAME = 'users-ping-manager';
const LEADER_ACTIVE_SIGNAL_INTERVAL = 15000;
const LEADER_INACTIVE_TIMEOUT = LEADER_ACTIVE_SIGNAL_INTERVAL * 3;
const PING_INTERVAL = 180000;
const LEADER_SELECTION_WINDOW = 120;
const LEADER_MONITOR_INTERVAL = 5000;

export class TabsPingManager {
    tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    startedAt = Date.now();
    ping: PingHandler;
    channel: BroadcastChannel | null = null;
    isLeader = false;
    isPaused = false;
    destroyed = false;
    leaderId: string | null = null;
    leaderLastActiveSignalAt = 0;
    leaderSelectionTimer: ReturnType<typeof setTimeout> | null = null;
    pingTimer: ReturnType<typeof setInterval> | null = null;
    leaderActiveSignalTimer: ReturnType<typeof setInterval> | null = null;
    leaderMonitorTimer: ReturnType<typeof setInterval> | null = null;
    leaderSelectionCandidates = new Map<string, { tabId: string, startedAt: number }>();

    constructor(ping: PingHandler) {
        this.ping = ping;
    }

    start() {
        if (typeof window === 'undefined') {
            return;
        }

        if (this.isPaused) {
            return;
        }

        if (typeof BroadcastChannel === 'undefined') {
            this.becomeLeader();
            return;
        }

        this.channel = new BroadcastChannel(CHANNEL_NAME);
        this.channel.addEventListener('message', this.handleMessage);
        window.addEventListener('pagehide', this.handlePageHide);
        window.addEventListener('beforeunload', this.handlePageHide);

        this.startLeaderSelection();

        this.leaderMonitorTimer = setInterval(() => {
            if (!this.hasFreshLeader()) {
                this.startLeaderSelection();
            }
        }, LEADER_MONITOR_INTERVAL);
    }

    stop() {
        this.destroyed = true;
        this.setLeaderInactive();
        this.clearLeaderSelectionTimer();
        this.clearMonitorTimer();

        if (this.channel) {
            this.channel.removeEventListener('message', this.handleMessage);
            this.channel.close();
            this.channel = null;
        }

        window.removeEventListener('pagehide', this.handlePageHide);
        window.removeEventListener('beforeunload', this.handlePageHide);
    }

    handleMessage = (event: MessageEvent<ChannelMessage>) => {
        const message = event.data;
        if (!message || message.tabId === this.tabId) {
            return;
        }

        if (message.type === 'ping-stop') {
            this.pauseUntilAuthorization(false);
            return;
        }

        if (this.isPaused) {
            return;
        }

        if (message.type === 'leader-active') {
            this.leaderId = message.tabId;
            this.leaderLastActiveSignalAt = Date.now();

            if (this.isLeader && this.isHigherPriorityThan(message)) {
                return;
            }

            if (this.isLeader) {
                this.setLeaderInactive(false);
            }
            this.clearLeaderSelectionTimer();
            return;
        }

        if (message.type === 'leader-inactive') {
            if (this.leaderId === message.tabId) {
                this.leaderId = null;
                this.leaderLastActiveSignalAt = 0;
            }

            this.startLeaderSelection();
            return;
        }

        if (message.type === 'leader-selection') {
            this.leaderSelectionCandidates.set(message.tabId, {
                tabId: message.tabId,
                startedAt: message.startedAt,
            });

            if (this.isLeader) {
                this.send('leader-active');
                return;
            }

            if (!this.hasFreshLeader()) {
                this.startLeaderSelection();
            }
        }
    };

    handlePageHide = () => {
        this.setLeaderInactive();
    };

    startLeaderSelection() {
        if (this.destroyed || this.isPaused) {
            return;
        }

        if (this.isLeader) {
            this.send('leader-active');
            return;
        }

        if (this.hasFreshLeader()) {
            return;
        }

        this.leaderSelectionCandidates.set(this.tabId, {
            tabId: this.tabId,
            startedAt: this.startedAt,
        });

        this.send('leader-selection');

        if (this.leaderSelectionTimer) {
            return;
        }

        this.leaderSelectionTimer = setTimeout(() => {
            this.leaderSelectionTimer = null;

            if (this.destroyed || this.hasFreshLeader()) {
                return;
            }

            const candidates = Array.from(this.leaderSelectionCandidates.values());

            this.leaderSelectionCandidates.clear();

            if (candidates.length === 0) {
                return;
            }

            candidates.sort((left, right) => {
                if (left.startedAt !== right.startedAt) {
                    return left.startedAt - right.startedAt;
                }
                return left.tabId.localeCompare(right.tabId);
            });

            if (candidates[0].tabId === this.tabId) {
                this.becomeLeader();
            }
        }, LEADER_SELECTION_WINDOW);
    }

    becomeLeader() {
        if (this.isLeader || this.destroyed || this.isPaused) {
            return;
        }

        this.isLeader = true;
        this.leaderId = this.tabId;
        this.leaderLastActiveSignalAt = Date.now();
        this.clearLeaderSelectionTimer();

        void this.runPing();
        this.send('leader-active');

        this.pingTimer = setInterval(() => {
            void this.runPing();
        }, PING_INTERVAL);

        this.leaderActiveSignalTimer = setInterval(() => {
            this.leaderLastActiveSignalAt = Date.now();
            this.send('leader-active');
        }, LEADER_ACTIVE_SIGNAL_INTERVAL);
    }

    setLeaderInactive(notify = true) {
        if (!this.isLeader) {
            return;
        }

        this.isLeader = false;
        this.clearPingTimer();
        this.clearLeaderActiveSignalTimer();

        if (notify) {
            this.send('leader-inactive');
        }

        if (this.leaderId === this.tabId) {
            this.leaderId = null;
            this.leaderLastActiveSignalAt = 0;
        }
    }

    hasFreshLeader() {
        if (!this.leaderId) {
            return false;
        }

        return Date.now() - this.leaderLastActiveSignalAt < LEADER_INACTIVE_TIMEOUT;
    }

    isHigherPriorityThan(message: Pick<ChannelMessage, 'startedAt' | 'tabId'>) {
        if (this.startedAt !== message.startedAt) {
            return this.startedAt < message.startedAt;
        }

        return this.tabId.localeCompare(message.tabId) < 0;
    }

    send(type: ChannelMessageType) {
        this.channel?.postMessage({
            type,
            tabId: this.tabId,
            startedAt: this.startedAt,
            timestamp: Date.now(),
        } satisfies ChannelMessage);
    }

    clearLeaderSelectionTimer() {
        if (this.leaderSelectionTimer) {
            clearTimeout(this.leaderSelectionTimer);
            this.leaderSelectionTimer = null;
        }
    }

    clearPingTimer() {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
    }

    clearLeaderActiveSignalTimer() {
        if (this.leaderActiveSignalTimer) {
            clearInterval(this.leaderActiveSignalTimer);
            this.leaderActiveSignalTimer = null;
        }
    }

    clearMonitorTimer() {
        if (this.leaderMonitorTimer) {
            clearInterval(this.leaderMonitorTimer);
            this.leaderMonitorTimer = null;
        }
    }

    runPing = async () => {
        try {
            const response = await this.ping();

            if (this.shouldPauseAfterPing(response)) {
                this.pauseUntilAuthorization();
            }
        } catch (error) {
            console.log('run ping', error);
        }
    };

    shouldPauseAfterPing(response: unknown) {
        if (!response || typeof response !== 'object' || !('isSuccess' in response)) {
            return false;
        }

        const { isSuccess, httpStatus } = response as { isSuccess?: boolean, httpStatus?: number };

        return isSuccess === false && httpStatus === 401;
    }

    pauseUntilAuthorization(notify = true) {
        if (this.isPaused) {
            return;
        }

        this.isPaused = true;
        this.clearLeaderSelectionTimer();
        this.clearMonitorTimer();
        this.setLeaderInactive(false);
        this.leaderId = null;
        this.leaderLastActiveSignalAt = 0;

        if (notify) {
            this.send('ping-stop');
        }
    }
}
